import { SITE_HTML } from '@app/formats'
import type { Said as SaidText } from '@app/shared'
import { useQuery } from '@tanstack/react-query'
import { placeLine, townLine } from '../lib/address-place'
import type { OrgListItem } from '../lib/org'
import { orgHref } from '../lib/public-router'
import { useTRPC } from '../lib/trpc'
import { HEBREW, STRONG } from '../ui/formats/reading-direction'
import { directionsHref, telHref } from '../ui/PublicShell'
import { RichTextView } from '../ui/RichTextView'
import { Said } from '../ui/Said'
import { GivingCards } from './GivingCards'
import { OtherWaysToGive } from './OtherWaysToGive'
import './shul-home.css'

/*
 * The bands an org's front door is built from whatever leads it: its name,
 * the ways to give, and how to reach it. A shul's front door puts what is next
 * at the shul above them; a site that leads with giving puts nothing there.
 */

/**
 * The page's heading as the org's own name — where nothing leads on a shul's
 * front door, and the whole of the heading on a site that leads with giving.
 * The page still says whose it is, and the ways in below it stand.
 */
export function NamedHeading({ name }: { name: SaidText }) {
  return (
    <section className="home__hero" aria-labelledby="home-hero">
      <div className="home__measure">
        {name.en ? (
          <h1 id="home-hero" className="home__headline">
            <Said said={name} />
          </h1>
        ) : (
          <h1
            id="home-hero"
            className="home__headline home__headline--he"
            lang="he"
            dir="rtl"
          >
            {name.he}
          </h1>
        )}
      </div>
    </section>
  )
}

/**
 * The ways to give, as the donate page's own top level has them: a donation,
 * membership where the org offers any, then each campaign collecting — those
 * with a closing date first, soonest first, as the server orders them.
 */
export function Give({ orgId }: { orgId: string }) {
  const trpc = useTRPC()
  const campaigns = useQuery(
    trpc.donation.campaigns.queryOptions({ shortId: orgId }),
  )
  const menu = useQuery(trpc.donation.menu.queryOptions({ shortId: orgId }))
  const tiers = useQuery(trpc.donation.tiers.queryOptions({ shortId: orgId }))
  const org = useQuery(trpc.donation.org.queryOptions({ shortId: orgId }))
  const sections = useQuery(
    trpc.site.publicSections.queryOptions({ shortId: orgId }),
  )

  /* The rows are drawn once all three have answered, and once the org's own
     sections above them have: anything arriving after the rest pushes the
     list down under a reader already aiming at a row. */
  if (
    campaigns.isLoading ||
    menu.isLoading ||
    tiers.isLoading ||
    org.isLoading ||
    sections.isLoading
  )
    return null

  // The org's own menu, named — never a sentence about what it offers.
  const offered = (menu.data ?? [])
    .filter((i) => !i.catchAll)
    .map((i) => i.label)
  const general = [
    {
      key: 'donate',
      name: 'Donation',
      line: offered.length ? offered.join(' · ') : null,
      act: 'Give',
      href: orgHref(orgId, 'donate'),
    },
    ...(tiers.data?.tiers.length
      ? [
          {
            key: 'membership',
            name: 'Membership',
            line: 'Join, or renew your membership.',
            act: 'Join or renew',
            href: orgHref(orgId, 'donate'),
          },
        ]
      : []),
  ]
  const funds = (campaigns.data ?? []).map((c) => ({
    key: c.shortId,
    name: c.label,
    line: c.tagline,
    appeal: c.appeal ?? null,
    goalCents: c.goalCents ?? null,
    pledgedCents: c.pledgedCents ?? null,
    act: 'Give',
    href: orgHref(orgId, 'donate', c.shortId),
  }))
  const other = org.data?.otherWaysToGive ?? []

  /* The cards look is one design across the band: the heading over a grid
     holding every way to give, the general ones first, and the other ways
     along its foot. A list of rows beside a grid of cards would be two looks
     on one band. */
  if (org.data?.givingLook === 'cards' && funds.length > 0)
    return (
      <section
        className="home__band home__give home__give--cards"
        aria-labelledby="home-give"
      >
        <div className="home__measure home__give-cards">
          <h2 id="home-give" className="home__h2">
            Support us
          </h2>
          <GivingCards
            cards={[
              ...general.map((g) => ({
                ...g,
                appeal: null,
                goalCents: null,
                pledgedCents: null,
              })),
              ...funds,
            ]}
          />
          <OtherWaysToGive items={other} />
        </div>
      </section>
    )

  return (
    <section className="home__band home__give" aria-labelledby="home-give">
      <div className="home__measure home__split">
        <div className="home__give-head">
          <h2 id="home-give" className="home__h2">
            Support us
          </h2>
          <OtherWaysToGive items={other} />
        </div>
        <ul className="home__ways">
          {[...general, ...funds].map((r) => (
            <li key={r.key}>
              <a className="home__way" href={r.href}>
                <span className="home__way-text">
                  <span className="home__way-name">{r.name}</span>
                  {r.line ? (
                    <span className="home__way-line">{r.line}</span>
                  ) : null}
                </span>
                <span className="home__way-act" aria-hidden>
                  {r.act}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

/**
 * The org's own sections, in the order it put them: each a band like the
 * others, headed by its title, its body set by this page's own stylesheet.
 */
export function Sections({ orgId }: { orgId: string }) {
  const trpc = useTRPC()
  const sections = useQuery(
    trpc.site.publicSections.queryOptions({ shortId: orgId }),
  )
  return (sections.data ?? []).map((s) => (
    <section
      key={s.id}
      className="home__band"
      aria-labelledby={`home-section-${s.id}`}
    >
      <div className="home__measure home__split">
        {HEBREW.test(STRONG.exec(s.title)?.[0] ?? '') ? (
          <h2
            id={`home-section-${s.id}`}
            className="home__h2 home__h2--he"
            lang="he"
            dir="rtl"
          >
            {s.title}
          </h2>
        ) : (
          <h2 id={`home-section-${s.id}`} className="home__h2">
            {s.title}
          </h2>
        )}
        <div className="home__words">
          <RichTextView html={s.body} allow={SITE_HTML} />
        </div>
      </div>
    </section>
  ))
}

/**
 * Where the org is and how to reach it. Headed by what the band is for, as
 * Support us is: the masthead already names the org, with a shul's rav's line
 * beneath the name while the page is at its top, or in the menu on a phone.
 */
export function Visit({ org }: { org: OrgListItem }) {
  const postal = placeLine(org)
  const street = org.addressLine?.trim() ?? ''
  const place = townLine(org)
  const phone = org.phone?.trim() ?? ''
  const email = org.email?.trim() ?? ''

  return (
    <section className="home__band home__visit" aria-labelledby="home-visit">
      <div className="home__measure home__split">
        <div className="home__visit-head">
          <h2 id="home-visit" className="home__h2">
            Contact us
          </h2>
        </div>
        <div className="home__visit-body">
          {postal ? (
            <address className="home__address">
              {street ? <span>{street}</span> : null}
              {place ? <span>{place}</span> : null}
              <a className="home__textlink" href={directionsHref(postal)}>
                Get directions
              </a>
            </address>
          ) : null}
          <div className="home__reach">
            {phone ? (
              <a className="home__phone" href={`tel:${telHref(phone)}`}>
                {phone}
              </a>
            ) : null}
            {email ? <a href={`mailto:${email}`}>{email}</a> : null}
            <a
              className="home__textlink"
              href={orgHref(org.shortId, 'contact')}
            >
              Send us a message
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}
